import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertTriangle,
  Loader2,
  RefreshCw,
  Trash2,
  RotateCcw,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SentinelCleanupPanel
 * --------------------
 * Super-Admin utility to clear SAM / eBuy sentinel data scoped to a single
 * client org. Useful for end-of-trial cleanup and dev resets.
 *
 * Flow:
 *  1. Pick org from dropdown (only orgs with sentinel data appear)
 *  2. Choose scope: SAM opps, eBuy opps, GridFS attachments, AI rank reset only
 *  3. Click "Preview Count" → shows live record counts
 *  4. Click "Execute Cleanup" → type CLEAR → confirms and runs
 */
export default function SentinelCleanupPanel() {
  const { authToken } = useAuth();

  // ── Org list ──────────────────────────────────────────────────────────────
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');

  // ── Scope options ─────────────────────────────────────────────────────────
  const [includeSam, setIncludeSam] = useState(true);
  const [includeEbuy, setIncludeEbuy] = useState(true);
  const [includeAttachments, setIncludeAttachments] = useState(false);
  const [resetAiOnly, setResetAiOnly] = useState(false);

  // ── Count preview ─────────────────────────────────────────────────────────
  const [counts, setCounts] = useState(null);
  const [countsLoading, setCountsLoading] = useState(false);

  // ── Confirmation dialog ───────────────────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [executing, setExecuting] = useState(false);

  const armed = typed.trim() === 'CLEAR';

  // ── Load orgs on mount ────────────────────────────────────────────────────
  useEffect(() => {
    loadOrgs();
  }, []);

  const loadOrgs = async () => {
    setOrgsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/danger/sentinel-cleanup/orgs`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrgs(data.orgs || []);
      }
    } catch (e) {
      toast.error('Failed to load org list');
    } finally {
      setOrgsLoading(false);
    }
  };

  // ── Preview counts ────────────────────────────────────────────────────────
  const handlePreview = async () => {
    if (!selectedOrgId) return;
    setCountsLoading(true);
    setCounts(null);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/danger/sentinel-cleanup/count?org_id=${encodeURIComponent(selectedOrgId)}`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      if (res.ok) {
        const data = await res.json();
        setCounts(data);
      } else {
        toast.error('Failed to fetch counts');
      }
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
    } finally {
      setCountsLoading(false);
    }
  };

  // ── Execute cleanup ───────────────────────────────────────────────────────
  const handleExecute = async () => {
    if (!armed || executing) return;
    setExecuting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/danger/sentinel-cleanup`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          confirm: 'CLEAR',
          org_id: selectedOrgId,
          include_sam: includeSam,
          include_ebuy: includeEbuy,
          include_attachments: includeAttachments,
          reset_ai_ranks_only: resetAiOnly,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(json.detail || 'Cleanup failed', { duration: 6000 });
        return;
      }
      const total = json.total ?? 0;
      if (total === 0) {
        toast.info('Nothing to clean — all selected collections already empty.');
      } else {
        const breakdown = Object.entries(json.deleted || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        toast.success(
          resetAiOnly
            ? `AI rank reset on ${total} record(s)`
            : `Deleted ${total} record(s)`,
          { description: breakdown, duration: 8000 },
        );
      }
      setDialogOpen(false);
      setTyped('');
      setCounts(null);
      // Refresh org list (org may now have zero data)
      loadOrgs();
    } catch (e) {
      toast.error(`Network error: ${e.message || 'unknown'}`);
    } finally {
      setExecuting(false);
    }
  };

  // ── Derived display helpers ───────────────────────────────────────────────
  const selectedOrgName = orgs.find((o) => o.id === selectedOrgId)?.name || selectedOrgId;

  const scopeLabel = () => {
    if (resetAiOnly) return 'Reset AI Rankings';
    const parts = [];
    if (includeSam) parts.push('SAM opps');
    if (includeEbuy) parts.push('eBuy opps');
    if (includeAttachments) parts.push('+ attachments');
    return parts.length ? parts.join(', ') : 'nothing selected';
  };

  const canPreview = !!selectedOrgId;
  const canExecute = !!selectedOrgId && (includeSam || includeEbuy);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Row 1 — Org picker */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs font-semibold text-slate-700 mb-1 block">
            Client Organization
          </Label>
          <Select value={selectedOrgId} onValueChange={(v) => { setSelectedOrgId(v); setCounts(null); }}>
            <SelectTrigger className="bg-white border-slate-200">
              <SelectValue placeholder={orgsLoading ? 'Loading…' : 'Select org with sentinel data'} />
            </SelectTrigger>
            <SelectContent>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name || o.id}
                </SelectItem>
              ))}
              {orgs.length === 0 && !orgsLoading && (
                <div className="px-3 py-2 text-xs text-slate-400">No orgs with sentinel data found</div>
              )}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={loadOrgs}
          disabled={orgsLoading}
          className="border-slate-200 text-slate-600"
        >
          {orgsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </Button>
      </div>

      {/* Row 2 — Scope checkboxes */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { id: 'sam', label: 'SAM Opportunities', state: includeSam, set: setIncludeSam, disabled: resetAiOnly },
          { id: 'ebuy', label: 'eBuy Opportunities', state: includeEbuy, set: setIncludeEbuy, disabled: resetAiOnly },
          { id: 'attachments', label: 'GridFS Attachments', state: includeAttachments, set: setIncludeAttachments, disabled: resetAiOnly },
          { id: 'aionly', label: 'Reset AI Ranks Only', state: resetAiOnly, set: (v) => { setResetAiOnly(v); if (v) setIncludeAttachments(false); } },
        ].map(({ id, label, state, set, disabled }) => (
          <label
            key={id}
            className={`flex items-center gap-2 text-xs cursor-pointer p-2 rounded border select-none transition-colors ${
              disabled
                ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'
                : state
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <input
              type="checkbox"
              className="accent-red-600"
              checked={state}
              disabled={disabled}
              onChange={(e) => set(e.target.checked)}
            />
            {label}
          </label>
        ))}
      </div>

      {/* Row 3 — Preview counts */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePreview}
          disabled={!canPreview || countsLoading}
          className="border-slate-300 text-slate-700 gap-1"
        >
          {countsLoading ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Eye className="w-3 h-3" />
          )}
          Preview Count
        </Button>

        {counts && (
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs border-blue-200 text-blue-700 bg-blue-50">
              SAM: {counts.sam_opps} opps · {counts.sam_ai_ranked} ranked · {counts.sam_attachments} attachments
            </Badge>
            <Badge variant="outline" className="text-xs border-purple-200 text-purple-700 bg-purple-50">
              eBuy: {counts.ebuy_opps} opps · {counts.ebuy_ai_ranked} ranked · {counts.ebuy_attachments} attachments
            </Badge>
          </div>
        )}
      </div>

      {/* Row 4 — Execute button */}
      <div>
        <Button
          variant="outline"
          size="sm"
          disabled={!canExecute}
          onClick={() => { setTyped(''); setDialogOpen(true); }}
          className="border-red-300 text-red-700 hover:bg-red-50 gap-1"
        >
          {resetAiOnly ? (
            <RotateCcw className="w-4 h-4" />
          ) : (
            <Trash2 className="w-4 h-4" />
          )}
          {resetAiOnly ? 'Reset AI Rankings' : 'Delete Sentinel Data'}
        </Button>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog
        open={dialogOpen}
        onOpenChange={(v) => { if (!executing) { setDialogOpen(v); if (!v) setTyped(''); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              {resetAiOnly ? 'Reset AI rankings?' : 'Delete sentinel data?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-3 mt-2">
                <div>
                  <span className="font-semibold">{selectedOrgName}</span>
                  {' — '}
                  {resetAiOnly
                    ? 'AI rank scores and reasoning will be cleared. Opportunities themselves are preserved.'
                    : (
                      <>
                        Scope: <span className="font-semibold">{scopeLabel()}</span>
                        {counts && (
                          <span className="text-slate-500">
                            {' '}({counts.sam_opps + counts.ebuy_opps} records
                            {includeAttachments ? `, ${counts.sam_attachments + counts.ebuy_attachments} attachments` : ''})
                          </span>
                        )}
                      </>
                    )
                  }
                </div>
                {!resetAiOnly && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-xs">
                    This action is <strong>irreversible</strong>. Deleted records cannot be
                    recovered. The action is recorded in the audit log.
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold text-slate-700">
                    Type <code className="bg-slate-100 px-1 rounded font-mono">CLEAR</code> to confirm:
                  </Label>
                  <Input
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder="CLEAR"
                    className="mt-1 font-mono"
                    autoFocus
                    disabled={executing}
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={executing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExecute}
              disabled={!armed || executing}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            >
              {executing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working…</>
              ) : resetAiOnly ? (
                'Reset now'
              ) : (
                'Delete now'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
